/* eslint-disable react-hooks/rules-of-hooks */
import React, { useRef, useState } from "react";
import PropTypes from "prop-types";
import { IoIosArrowBack } from "@react-icons/all-files/io/IoIosArrowBack";
import { IoIosArrowForward } from "@react-icons/all-files/io/IoIosArrowForward";
import isSticky from "~hooks/stickyOnScroll";
import keygen from "~helpers/keygen";

import useTranslate from "~hooks/useTranslate/useTranslate";
import styles from "./anchor.module.scss";

function Anchor({
  module: { items },
  isFixed = true,
  stickyOffset = 0,
  icon = null,
  showTitle = true,
  exclOperator = false,
  minCharactersForButtons = 86,
  setShowButtons = false,
  buttonScroll = 200,
  leftButtonIcon = <IoIosArrowBack />,
  rightButtonIcon = <IoIosArrowForward />,
  hasAdmin = false,
  offset = 132,
}) {
  const [scrollX, setScrollX] = useState(0);
  const [scrollEnd, setScrollEnd] = useState(false);
  
  const itemsRef = useRef([]);
  const anchorContainerRef = useRef(null);
  const anchorListRef = useRef(null);
  const progressBar = useRef(null);

  let numberOfCharacters = 0;

  items.forEach((anchor) => {
    numberOfCharacters += anchor?.label?.length;
  });
  const showButtons =
    numberOfCharacters > minCharactersForButtons && setShowButtons;

  const updateButtons = (shift) => {
    setScrollEnd(
      Math.floor(
        anchorListRef.current.scrollWidth -
          (anchorListRef.current.scrollLeft + shift)
      ) <= anchorListRef.current.offsetWidth
    );
  };

  const scroll = (shift) => {
    anchorListRef.current.scrollTo({
      left: anchorListRef.current.scrollLeft + shift,
      behavior: "smooth",
    });
    updateButtons(shift);
    setScrollX(scrollX + shift);
  };

  const setActiveAnchor = () => {
    const elements = document.querySelectorAll("[data-track='true'], [data-anchor-label]");
    const { innerHeight, scrollY } = window;
    const { scrollHeight } = document.body;

    // Update progress bar
    const calcPercent = (scrollY * 100) / (scrollHeight - innerHeight);
    if (progressBar.current) {
      progressBar.current.style.width = `${calcPercent}%`;
    }

    // Get visible elements
    const visibleElements = Array.from(elements)
      .filter((el) => el.getBoundingClientRect().top >= 0)
      .map((el) => el.id);

    // Reset active classes
    itemsRef.current.forEach((li) =>
      li?.parentNode.classList.remove(styles.active)
    );

    if (visibleElements.length > 0) {
      const activeElement = itemsRef.current.find(
        (el) => el?.getAttribute("href") === `#${visibleElements[0]}`
      );

      if (activeElement) {
        activeElement.parentNode.classList.add(styles.active);

        const { offsetWidth: elementWidth, offsetLeft: elementLeft } =
          activeElement;
        const containerWidth = anchorContainerRef.current.offsetWidth;

        anchorListRef.current.scrollLeft =
          elementLeft > containerWidth - elementWidth
            ? elementLeft + elementWidth + 5 - containerWidth
            : 0;
      }
    }
  };

  const sticky =
    isFixed && isSticky(stickyOffset, setActiveAnchor, anchorContainerRef);

  const anchorItems = items?.map((item, index) => (
    // eslint-disable-next-line react/no-array-index-key
    <li key={index} className={styles.anchorItem || ""}>
      <a
        className={`${styles.link || ""} anchor-carousel-gtm anchor-menu-gtm`}
        key={keygen()}
        draggable="false"
        // eslint-disable-next-line no-return-assign
        ref={(el) => (itemsRef.current[index] = el)}
        href={`#${item?.id && item.id}`}
        onClick={(e) => {
          e.preventDefault();
          const el = document.getElementById(item.id);
          if (el) {
            const y = el.getBoundingClientRect().top + window.pageYOffset - offset;
            window.scrollTo({ top: y, behavior: "smooth" });
          }
        }}
      >
        {useTranslate(item.slug, item.label)}
        {icon}
      </a>
    </li>
  ));

  return (
    <div className={styles.containerAnchor || ""} ref={anchorContainerRef}>
      {showTitle && (
        <div className={`${styles.anchorTitle || ""}`}>
          <span>{useTranslate("anchor_title", "Table of Contents")}</span>
        </div>
      )}

      <div className={`${sticky ? styles.stickyContainer || "" : styles.defaultConatiner || ""} ${hasAdmin ? styles.hasAdminBar || '' : ''} ${exclOperator ? styles.usingExclOperator || "" : ""}`}>
        {showButtons && (
          <button
            className={`${styles.buttLeft} ${
              scrollX <= 0 ? styles.disabled : ""
            }`}
            onClick={() => scroll(-buttonScroll)}
            type="button"
            title="anchorButtonLeft"
          >
            {leftButtonIcon}
          </button>
        )}
        <ul ref={anchorListRef} className={`${styles.anchor || ""}`}>
          {anchorItems}
        </ul>
        {showButtons && (
          <button
            className={`${styles.buttRight} ${
              !scrollEnd ? "" : styles.disabled
            }`}
            onClick={() => scroll(buttonScroll)}
            type="button"
            title="anchorButtonRight"
          >
            {rightButtonIcon}
          </button>
        )}
        {sticky && (
          <div ref={progressBar} className={styles.progressBar || ""} />
        )}
      </div>
    </div>
  );
}

Anchor.propTypes = {
  exclOperator: PropTypes.bool,
  module: PropTypes.shape({
    items: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string,
      })
    ),
  }).isRequired,
  styles: PropTypes.shape({}),
  isFixed: PropTypes.bool,
  icon: PropTypes.element,
  showTitle: PropTypes.bool,
  stickyOffset: PropTypes.number,
  buttonScroll: PropTypes.number,
  leftButtonIcon: PropTypes.shape({}),
  rightButtonIcon: PropTypes.shape({}),
  minCharactersForButtons: PropTypes.number,
  setShowButtons: PropTypes.bool,
  hasAdmin: PropTypes.bool,
  offset: PropTypes.number,
};

export default Anchor;
